/**
 * @prettier
 */

// React Packages
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withTranslation } from "react-i18next";

// Actions
import {
    authenticateAcuant,
    setInstanceID,
} from "../../state/actions/configActions";
import { clearSubmissionState } from "../../state/actions/submissionStateActions";

// Services
import DataDogService from "../../services/shared/datadogService";
import logService from "../../services/shared/logService";
import selectStorage from "../../services/shared/selectStorage";

// Components
import Header from "../../components/Header";
import Navigator from "../../components/Navigator";
import ReCapturePage from "../../pages/RecapturePage";
import { LoadingStateAnimation } from "../../components/ProcessingLayout";

// Config
import { areCookiesEnabled, isInIframe } from "../../services/shared/helpers";

// Helpers
import { genericErrors, needsAcuantSDK } from "../../services/shared/helpers";

function applyBrandPrimaryColor(primaryColor) {
    const { registerProperty } = window.CSS || {};
    if (registerProperty) {
        try {
            registerProperty({
                name: "--brand",
                syntax: "<color>",
                inherits: false,
                initialValue: primaryColor,
            });
        } catch (error) {
            if (error.name !== "InvalidModificationError") {
                logService.error("CSS.registerProperty failed for --brand", {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                });
                DataDogService.error(error);
            }
        }
    }
    document.documentElement.style.setProperty("--brand", primaryColor);
}

class AppEntryPage extends Component {
    constructor(props) {
        super(props);
        this.primaryFocusRef = React.createRef();
        this.loadingRef = React.createRef();
        this.state = {
            inputValue: null,
            uuidExists: false,
            ruuidExists: false,
            brandingExists: null,
            loadingError: null,
            navigation: {
                action: "load",
                props: null,
            },
        };
    }

    componentDidMount() {
        this.props.clearSubmissionState();

        // Checks that cookies are enabled before querying selectStorage(areCookiesEnabled())
        areCookiesEnabled() &&
            selectStorage(areCookiesEnabled()).removeItem("documentRegion");
        const uuid = areCookiesEnabled()
            ? selectStorage(areCookiesEnabled()).getItem("uuid")
            : null;
        const ruuid = areCookiesEnabled()
            ? selectStorage(areCookiesEnabled()).getItem("ruuid")
            : null;

        if (ruuid !== null) {
            this.setState({
                ruuidExists: true,
            });
        }

        if (uuid !== null && uuid.length) {
            this.setState({
                uuidExists: true,
            });
        }

        DataDogService.initialise();
        this.props.setInstanceID(this);
        areCookiesEnabled();

        this.getErrorMessage();

        // Sets focus to primary heading on first render
        if (this.primaryFocusRef && this.primaryFocusRef.current) {
            this.primaryFocusRef.current.focus();
        }

        // Sets the document title
        const { t } = this.props;
        if (this.loadingRef && this.loadingRef.current) {
            document.title = t("idpal_doc_title_initialisation");
        } else {
            document.title = t("idpal_doc-title_initialisation_error");
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        // Authenticate Acuant if necessary
        if (
            prevProps.submissionState.submissionState !==
                this.props.submissionState.submissionState &&
            selectStorage(areCookiesEnabled()).getItem("submissionid") !==
                null &&
            !this.props.groupLink
        ) {
            if (
                needsAcuantSDK(this.props.submissionState) &&
                !this.props.acuantInitialized
            ) {
                // Main SDK
                const sdk = document.createElement("script");
                sdk.src = "/acuant/AcuantJavascriptWebSdk.min.js?v=20221129";
                sdk.async = true;
                sdk.onload = () => window.loadAcuantSdk();
                document.body.appendChild(sdk);

                // Acuant Camera SDK
                const acuantCameraSdk = document.createElement("script");
                acuantCameraSdk.src = "/acuant/AcuantCamera.min.js?v=20230809";
                acuantCameraSdk.async = true;
                document.body.appendChild(acuantCameraSdk);

                // Open CV
                const opencv = document.createElement("script");
                opencv.src = "/acuant/opencv.min.js";
                opencv.async = true;
                document.body.appendChild(opencv);

                this.props.authenticateAcuant();
            }
        }
        if (
            prevProps.uuidValid !== this.props.uuidValid ||
            prevProps.limitReached !== this.props.limitReached ||
            prevProps.isBranding !== this.props.isBranding ||
            prevProps.isUuidFromRuuidError !==
                this.props.isUuidFromRuuidError ||
            prevProps.groupLink !== this.props.groupLink ||
            prevProps.submissionState.submissionState !==
                this.props.submissionState.submissionState
        ) {
            this.getErrorMessage();
        }

        if (
            prevProps.branding !== this.props.branding &&
            !this.state.brandingExists
        ) {
            this.setState({
                brandingExists: this.props.branding,
            });
            applyBrandPrimaryColor(
                `#${this.props.branding.data.company_branding.primary_color}`
            );
        }
        if (
            areCookiesEnabled() &&
            !selectStorage(areCookiesEnabled()).getItem("ruuid") &&
            this.state.ruuidExists
        ) {
            this.setState({
                ruuidExists: false,
            });
        }

        // Sets the document title
        const { t } = this.props;
        if (!this.state.ruuidExists) {
            if (this.loadingRef && this.loadingRef.current) {
                document.title = t("idpal_doc_title_initialisation");
            } else {
                document.title = t("idpal_doc-title_initialisation_error");
            }
        }
    }

    getErrorMessage() {
        let message = null;

        // Check if we have ruuid, uuid or submissionID in storage
        const isInitWithRuuid =
            selectStorage(areCookiesEnabled()).getItem("ruuid") !== null;
        const isInitWithUuid =
            selectStorage(areCookiesEnabled()).getItem("uuid") !== null;
        const isInitWithSubmissionID =
            selectStorage(areCookiesEnabled()).getItem("submissionid") !== null;

        // First check if cookies are enabled and not in iframe
        if (
            (!areCookiesEnabled() && !isInIframe()) ||
            (!areCookiesEnabled() && isInitWithRuuid)
        ) {
            message = "idpal_cookie_error";
            this.setState({ loadingError: message });
            return;
        }

        // Check if Ruuid
        if (isInitWithRuuid) {
            // Check we have branding
            if (!this.props.isBranding) {
                message = "idpal_default_error";
                this.setState({ loadingError: message });
                return;
            }

            // Check we have no error getting uuid
            if (this.props.isUuidFromRuuidError) {
                if (genericErrors()) {
                    message = "idpal_default_error";
                } else {
                    message = "idpal_ruuid_error";
                }
                this.setState({ loadingError: message });
                return;
            }
        } else {
            // Check we have a valid uuid
            if (!this.props.uuidValid || !isInitWithUuid) {
                message = "idpal_uuid_invalid_error";
                this.setState({ loadingError: message });
                return;
            }

            // Check we have not reached the uuid limit
            if (this.props.limitReached) {
                message = "idpal_submission_limit_error";
                this.setState({ loadingError: message });
                return;
            }

            // Check acuant has initialized
            if (!isInitWithSubmissionID && !this.props.groupLink) {
                message = "idpal_initialisation_error";
                this.setState({ loadingError: message });
                return;
            }
        }

        this.setState({ loadingError: message });
    }

    render() {
        const { t } = this.props;

        // Applied loading screen, before getting branding details
        if (this.props.pendingItems > 0) {
            return (
                <div className="o-site-wrap entry-loading">
                    <LoadingStateAnimation />
                </div>
            );
        }

        // Direct to recapture screen is no error & ruuid
        if (this.state.ruuidExists && !this.state.loadingError) {
            return <ReCapturePage />;
        }

        return (
            <div className="root-layout-container error-page">
                <Header showLanguageSelect />

                <div>
                    {this.state.loadingError && (
                        <Fragment>
                            <div className="error-content">
                                <h1
                                    className="error-title"
                                    ref={this.primaryFocusRef}
                                    tabIndex={0}
                                >
                                    {t(this.state.loadingError)}
                                    {this.props.isUuidFromRuuidError &&
                                        !genericErrors() && (
                                            <span>
                                                {
                                                    this.props.branding.data
                                                        .company_branding
                                                        .costumer_support_email
                                                }
                                            </span>
                                        )}
                                </h1>
                            </div>

                            <Navigator
                                page={"start_screen"}
                                action={this.state.navigation.action}
                                propsToPass={this.state.navigation.props}
                            />
                        </Fragment>
                    )}

                    {!this.state.ruuidExists &&
                        !this.props.isInitWithRuuid &&
                        !this.state.loadingError &&
                        this.props.instanceID &&
                        this.state.brandingExists && (
                            <Fragment>
                                {DataDogService.log(
                                    "Application Initialised Successfully."
                                )}
                                <Navigator page={"app_entry"} action={"next"} />
                            </Fragment>
                        )}

                    {/*skip start screen when ruuid*/}
                    {!this.state.ruuidExists &&
                        this.props.isInitWithRuuid &&
                        !this.state.loadingError &&
                        this.props.instanceID &&
                        this.state.brandingExists && (
                            <Fragment>
                                {DataDogService.log(
                                    "Application Initialised Successfully."
                                )}
                                <Navigator
                                    page={"start_screen"}
                                    action={"next"}
                                />
                            </Fragment>
                        )}
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        acuantInitialized: state.config.acuantInitialized,
        instanceID: state.config.instanceID,
        branding: state.config.profile,
        isBranding: state.config.isBranding,
        uuidValid: state.config.uuidValid,
        isUuidFromRuuidError: state.config.isUuidFromRuuidError,
        isRuuid: state.config.isRuuid,
        isInitWithRuuid: state.config.isInitWithRuuid,
        limitReached: state.config.limitReached,
        pendingItems: state.spinner.pendingItems,
        submissionState: state.submissionState,
        groupLink: state.config.groupLink,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            authenticateAcuant,
            setInstanceID,
            clearSubmissionState,
        },
        dispatch
    );
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(AppEntryPage)
);
